import {
  Component,
  OnInit,
  Input,
  OnChanges,
  SimpleChanges,
  Output,
  OnDestroy,
  EventEmitter,
  Inject,
  Optional
} from '@angular/core';
import { Channel } from 'stream-chat';
import { ProductsService, Product, Negotiation, Shipping, ChatChannel, ChatUser } from '@vestiaire/api-sdk-angular';
import { Observable, Subject, Subscription } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { IAppState } from '@app/store';

import { ChatService } from '@app/+chat/services/chat.service';
import { MmaoService } from '@app/shared/components/mmao/services/mmao.service';
import { selectReservationState } from '@app/+product/store/reducers';
import { ProductReservation } from '@vestiaire/api-sdk-angular/model/productReservation';
import { ReservationService } from '@app/shared/services/product';
import { CheckReservation } from '../../../+product/store/actions/reservation.action';
import * as fromUser from '@app/core/user/store/reducers';
import { TrackingService } from '@app/analytics/services/tracking.service';
import { TagContainer } from '@app/analytics/models/container';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface DialogData {
  product: Product;
  channelID: string;
  chatFromPdp: boolean;
}

@Component({
  selector: 'vc-chat-discussion',
  templateUrl: './chat-discussion.component.html',
  styleUrls: ['./chat-discussion.component.scss']
})
export class ChatDiscussionComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  channelId: string;
  @Input()
  openChatFromLike: boolean;

  channel: Channel;
  userId: string;
  receiverId: string;
  buyerId: string;
  sellerId: string;
  isSeller: boolean;
  product: Product;
  shippingInformation$: Observable<Shipping>;
  negotiation$: Observable<Negotiation>;
  receiver: ChatUser;
  email = '';

  senderIsBlocked = false;
  receiverIsBlocked = false;
  ngUnsubscribe = new Subject<void>();
  reservation: ProductReservation;
  productId: string;
  senderIsBanned: boolean;
  receiverIsBanned: boolean;
  chatConnectionListener: Subscription;

  @Output() displayListMobile: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    private chatService: ChatService,
    private productService: ProductsService,
    private mmaoService: MmaoService,
    private reservationService: ReservationService,
    private store: Store<IAppState>,
    private trackingService: TrackingService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: DialogData,
    @Optional() public dialogRef: MatDialogRef<ChatDiscussionComponent>
  ) {}

  ngOnInit() {
    this.chatService.init();
    if (this.data?.channelID) {
      this.channelId = this.data.channelID;
    }
    this.store.pipe(select(selectReservationState), takeUntil(this.ngUnsubscribe)).subscribe(
      state => {
        const reservation = state && state.productId === this.productId ? state.status : null;
        this.reservation = reservation;
      },
      _ => (this.reservation = null)
    );

    this.chatConnectionListener = this.chatService.connectionState$.subscribe(
      res => {
        if (res) {
          // stream status is active
          this.createChannel();
          this.userId = this.chatService.streamChat.user.id;
        }
      },
      err => console.log('[connectionState] err', err)
    );

    this.store.pipe(select(fromUser.getUserEmail), takeUntil(this.ngUnsubscribe)).subscribe(
      email => (this.email = email),
      error => console.error(error)
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    const { channelId } = changes;

    if (channelId && channelId.currentValue && !channelId.isFirstChange()) {
      this.channel = null;
      this.senderIsBlocked = false;
      this.receiverIsBlocked = false;
      this.createChannel();
    }
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.chatConnectionListener.unsubscribe();
  }

  createChannel() {
    // getting product id from the channel id ex. channelId = '8548534-7683632'
    // Since we are reusing the chat discussion component when opened from notification bell and PDP page,
    // in order to pass payload(buyerId, sellerId, productId) to create channel vc api, retrieve it before from product details
    // channelIDSplit[1] is the product id
    const channelIDSplit = this.channelId?.split('-');
    if (channelIDSplit?.length > 0) {
      this.productService
        .getProduct(channelIDSplit[1])
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          result => {
            this.product = result.data;
            this.store.dispatch(new CheckReservation({ productId: this.product.id }));

            // payload required for create channel VC api
            const payload = {
              buyerId: this.product.seller?.id === this.userId ? channelIDSplit[0] : this.userId,
              sellerId: this.product.seller?.id,
              productId: channelIDSplit[1]
            };
            const { buyerId, sellerId, productId } = payload;
            if (buyerId && sellerId && productId) {
              this.chatService
                .createChannel(payload)
                .then(res => {
                  this.processChannelInfo(res);
                  // join stream channel
                  this.chatService.joinChannel(this.channelId).then(response => {
                    this.processStreamChannelInfo(response);
                  });
                })
                .catch(err => {
                  console.log('[Create] channel api error', err);
                });
            }
          },
          () => console.log(`[getProduct] error in getting product details for id ${channelIDSplit[1]}`)
        );
    }
  }

  processStreamChannelInfo(result: Channel) {
    this.channel = result;
    this.chatService
      .getBlockedMembers(this.channel)
      .then(bannedUsers => {
        this.senderIsBlocked = bannedUsers.includes(this.userId);
        this.receiverIsBlocked = bannedUsers.includes(this.receiverId);
      })
      .catch(_ => {});

    this.chatService
      .getBannedMembers(this.channel)
      .then(bannedUsers => {
        this.senderIsBanned = bannedUsers.includes(this.userId);
        this.receiverIsBanned = bannedUsers.includes(this.receiverId);
      })
      .catch(_ => {});
  }

  processChannelInfo(res: ChatChannel) {
    this.productId = res.product;
    this.buyerId = res.buyer.id;
    this.sellerId = res.seller.id;
    this.isSeller = this.sellerId === this.userId;
    this.receiverId = this.isSeller ? this.buyerId : this.sellerId;

    // Shipping information
    this.shippingInformation$ = this.productService
      .getProductShippingInformation(this.productId, res.buyer.country)
      .pipe(map(r => r.data));

    // Receiver User information
    this.receiver = this.userId === this.sellerId ? res.buyer : res.seller;

    // Negotiation data
    if (this.isSeller) {
      this.negotiation$ = this.mmaoService
        .getNegotiationsByProduct(this.productId)
        .pipe(map(negotiations => negotiations.find(negotiation => negotiation.buyer.id === this.buyerId)));
    } else {
      this.negotiation$ = this.mmaoService.getActiveBuyerNegotiation(this.productId);
    }
  }

  trackingChat(origin: 'report' | 'message_sent') {
    const chat_buyer_page = this.data?.chatFromPdp ? 'product_buyer' : 'mmao_buyer';
    const chat_part = this.isSeller ? 'mmao_seller' : chat_buyer_page;
    const payload = {
      event: 'bs_chat',
      chat_action_name: origin ? origin : '',
      chat_context: chat_part
    };

    this.trackingService.send({
      type: 'event',
      container: TagContainer.Analytics,
      payload
    });
  }

  get isReserved(): boolean {
    return this.reservation && this.reservationService.isValid(this.reservation);
  }
}
